import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Clock, CheckCircle2, AlertCircle, Ticket, Users, MapPin, Bus, CreditCard, Smartphone, Landmark, Wallet } from "lucide-react";
import { readBusBooking, saveBusBooking } from "./Busbooking";
import { bookTicket } from "../../../../services/busApi";
import { requestSessionExpiry, useAuth } from "../../../../AuthProvider/AuthContext";
import "./Busconfirm.css";

const API_BASE =
  (process.env.REACT_APP_API_URL || "https://makebeetrip.com")
    .replace(/\/+$/, "") + "/api";

const getStoredToken = () => {
  try {
    return (
      localStorage.getItem("mbt_token") ||
      localStorage.getItem("token") ||        // ← add your real key here
      localStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
};

async function saveConfirmedBusBooking(booking, payment, supplierResponse) {
  const token = getStoredToken();
  if (!token) throw new Error("Please log in to save this booking.");

  const res = await fetch(`${API_BASE}/bus-bookings`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      booking,
      payment,
      supplierResponse,
      status: "upcoming",
    }),
  });

  const body = await res.json().catch(() => ({}));
  if (res.status === 401 || /invalid token/i.test(String(body.error || body.message || ""))) {
    requestSessionExpiry();
  }
  if (!res.ok && res.status !== 409) {
    throw new Error(body.error || "Could not save bus booking.");
  }
  return body;
}

function supplierBookingReference(data, fallback) {
  const supplier = data?.data || data || {};
  return (
    supplier.bookingId ||
    supplier.bookingReference ||
    supplier.ticketNo ||
    supplier.pnr ||
    fallback
  );
}

const loadEasebuzzCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.EasebuzzCheckout) {
      resolve(window.EasebuzzCheckout);
      return;
    }

    const existing = document.querySelector("script[data-easebuzz-checkout]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.EasebuzzCheckout), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";
    script.async = true;
    script.dataset.easebuzzCheckout = "true";
    script.onload = () => resolve(window.EasebuzzCheckout);
    script.onerror = () => reject(new Error("Could not load payment gateway. Please try again."));
    document.body.appendChild(script);
  });

// ─── helpers ────────────────────────────────────────────────────────────────
const splitCity = (v) => String(v || "").split(",")[0] || "—";
const money     = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

function formatDate(d) {
  if (!d) return "—";
  if (d.label) return `${d.label}${d.day ? `, ${d.day}` : ""}`;
  if (typeof d === "string") {
    const dt = new Date(`${d}T00:00:00`);
    if (!isNaN(dt))
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", weekday: "short" });
  }
  return String(d);
}

const extractPoint = (p) => {
  if (!p) return "";
  if (typeof p === "string") return p;
  return p.cityPointName ?? p.name ?? p.location ?? p.address ?? "";
};

function useCountdown(seconds) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (remaining <= 0) return;
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return { display: `${mm}:${ss}`, expired: remaining <= 0, remaining };
}

// ─── Success Popup ──────────────────────────────────────────────────────────
function SuccessPopup({ booking, txnid, onClose }) {
  const { bus, route, seats = [], price = {}, travellers = [], contact = {} } = booking;
  const seatCodes = seats.map((s) => s.code).join(", ");

  return (
    <div className="bc-overlay" role="dialog" aria-modal="true" aria-label="Booking confirmed">
      <div className="bc-popup">
        {/* animated checkmark */}
        <div className="bc-popup-icon-wrap">
          <svg className="bc-checkmark" viewBox="0 0 52 52">
            <circle className="bc-checkmark-circle" cx="26" cy="26" r="25" fill="none" />
            <path  className="bc-checkmark-check"  fill="none" d="M14 27l8 8 16-16" />
          </svg>
        </div>

        <h2 className="bc-popup-title">Booking Confirmed!</h2>
        <p className="bc-popup-sub">
          Your seats are locked in. Confirmation details have been sent to{" "}
          <strong>{contact.email || "your email"}</strong>.
        </p>

        <div className="bc-popup-ref">
          <span>Booking ID</span>
          <strong>{txnid || booking.bookingReference || "BUS-" + Date.now()}</strong>
        </div>

        <div className="bc-popup-summary">
          <div className="bc-popup-row">
            <Bus size={14} />
            <span>{splitCity(route?.from)} → {splitCity(route?.to)}</span>
          </div>
          <div className="bc-popup-row">
            <Ticket size={14} />
            <span>Seats: {seatCodes || "—"}</span>
          </div>
          <div className="bc-popup-row">
            <Users size={14} />
            <span>
              {travellers.map((t) => `${t.title} ${t.firstName} ${t.lastName}`).join(" · ") || `${seats.length} Passenger(s)`}
            </span>
          </div>
          <div className="bc-popup-row bc-popup-row--price">
            <CreditCard size={14} />
            {/* Prefer the server-priced payable stored with the session so the
                confirmation shows the amount actually charged (supplier fare +
                admin fees/taxes), falling back to the supplier total. */}
            <span>Amount Paid: <strong>{money(Number(readBusBooking()?.payableTotal || price.grandTotal))}</strong></span>
          </div>
        </div>

        <div className="bc-popup-actions">
          <button className="bc-popup-btn bc-popup-btn--primary" onClick={onClose}>
            View My Bookings
          </button>
          <button className="bc-popup-btn bc-popup-btn--ghost" onClick={() => window.print()}>
            Print Ticket
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function BusConfirm() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { expireSession } = useAuth();
  const booking   = location.state?.booking ?? readBusBooking();
 const countdown = useCountdown(8 * 60);

  const [paying,      setPaying]      = useState(false);
  const [payError,    setPayError]    = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmedTxn, setConfirmedTxn] = useState("");

  // ── Server-authoritative price quote (bus pricing) ───────────────────────
  // Created on this review screen so the customer sees the FINAL payable
  // (supplier fare + admin-configured fees/taxes) BEFORE paying. The backend
  // also derives the Easebuzz amount from this quote — the client-supplied
  // amount is ignored server-side. The quoteId is persisted with the session
  // so the post-payment booking save can consume it and store the immutable
  // pricing snapshot.
  const [pricingQuote, setPricingQuote] = useState(null);
  const [pricingError, setPricingError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!booking?.price?.grandTotal) return undefined;

    (async () => {
      try {
        const token = getStoredToken();
        const res = await fetch(`${API_BASE}/bus-bookings/quote`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
  supplierFareTotal: Number(booking.price.grandTotal ?? 0),
  supplierTaxTotal: Number(booking.price.gst ?? 0),
  baseFare: Number(booking.price.baseFare ?? 0),
  gst: Number(booking.price.gst ?? 0),
  discount: Number(booking.price.discount ?? 0),

  seats: (booking.seats || []).map((s) => ({
    code: s.code,
    price: s.price,
  })),

  passengerCount:
    booking.passengerCount ?? (booking.seats || []).length,

  seatCount: (booking.seats || []).length,
  travelDate: booking.route?.date,
  busId: booking.busId,

  // The backend /quote handler expects searchKey.
  searchKey: booking.searchKey || booking.searchTrackId || null,
}),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.quoteId) {
          throw new Error(data?.error || "Could not price this booking.");
        }
        setPricingQuote(data);
        setPricingError("");
        saveBusBooking({
          ...booking,
          quoteId: data.quoteId,
          payableTotal: data.payableTotal,
        });
      } catch (err) {
        if (cancelled) return;
        setPricingError(
          err?.message || "Could not calculate the final price. Please try again."
        );
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.price?.grandTotal]);

  // ── Check for Easebuzz return ───────────────────────────────────────────
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const status    = params.get("payment_status") || params.get("status");
    const txnid     = params.get("txnid")  || params.get("transaction_id");
    const easeTxn   = params.get("easepayid");

    if (status === "success" && txnid) {
  // Clean URL
  window.history.replaceState({}, "", window.location.pathname);

  const saved = readBusBooking();

  confirmBooking(txnid, easeTxn, saved?.blockId)
    .then(() => {
      setConfirmedTxn(txnid);
      setShowSuccess(true);
    })
    .catch((err) => {
      console.error("[BUS CONFIRM][RETURN] Ticket confirmation failed:", err);
      setPayError(
        err?.message ||
        "Payment succeeded, but ticket confirmation failed. Please contact support with your transaction ID."
      );
    });
} else if (status === "failure" || status === "userCancelled") {
      window.history.replaceState({}, "", window.location.pathname);
      setPayError("Payment was not completed. Please try again.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Server confirm + save to DB ────────────────────────────────────────
const confirmBooking = useCallback(async (txnid, easepayid, blockId) => {
    const saved = readBusBooking();
    if (!saved) return;

    const effectiveBlockId = blockId || saved.blockId;
    

    if (!effectiveBlockId) {
      throw new Error("Missing block reference — please restart the booking.");
    }

    // ── Confirm the ticket with the supplier — must succeed, no silent fallback ──
    const supplierData = await bookTicket({ blockKey: effectiveBlockId });

    const bookingReference =
      supplierBookingReference(supplierData, txnid) || txnid;

    const confirmed = {
      ...saved,
      status:            "upcoming",
      txnid,
      easepayid,
      bookingReference,
      supplierBookingId: bookingReference,
      supplierResponse:  supplierData,
      blockId:           effectiveBlockId,
    };

    // ── Save to your DB ──────────────────────────────────────────────────
    await saveConfirmedBusBooking(
      confirmed,
      {
        txnid,
        easepayid,
        paymentStatus:  "success",
        paymentMethod:  "easebuzz",
        amount:         saved.price?.grandTotal,
      },
      supplierData
    );

    saveBusBooking(confirmed);
    return confirmed;
  }, []);

  // ── Easebuzz initiate ──────────────────────────────────────────────────
  const handlePay = async () => {
    if (!booking?.blockId) { setPayError("Session invalid — please restart booking."); return; }
    if (countdown.expired) { setPayError("Session expired. Please go back and search again.");  return; }

    setPaying(true);
    setPayError("");

    try {
      const { bus, route, seats = [], price = {}, travellers = [], contact = {}, blockId } = booking;
      const token = getStoredToken();

      if (!token) {
        throw new Error("Please log in to continue payment.");
      }

      if (!pricingQuote?.quoteId) {
        throw new Error(
          pricingError ||
            "The final price is not ready yet. Please try again in a moment."
        );
      }

      const initRes = await fetch(`${API_BASE}/payment/easebuzz/initiate`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // server-priced: the backend derives the amount from the saved
          // pricing quote (the client value below is ignored server-side)
          service:          "bus",
          quoteId:          pricingQuote.quoteId,
          amount:           pricingQuote.payableTotal,
          productinfo: "BusBooking",
          firstname:        travellers[0]?.firstName ?? contact.name ?? "Traveller",
          email:            contact.email,
          phone:            contact.phone,
          bookingReference: booking.blockId ?? booking.busId ?? booking.id,
          udf1:             blockId ?? "",
          udf2:             booking.searchTrackId ?? "",
          udf3:             seats.map((s) => s.code).join(","),
        }),
      });

      if (initRes.status === 401) {
        expireSession?.();
        throw new Error("Your login session expired. Please log in again to continue payment.");
      }

      const initData = await initRes.json();
      



      if (!initRes.ok || !initData.access_key) {
        throw new Error(initData.error ?? "Could not initiate payment.");
      }

      // Save state before leaving page
      saveBusBooking({
  ...booking,
  blockId: booking.blockId,   // ← explicitly preserve it
  quoteId: pricingQuote.quoteId,      // consumed by the post-payment save
  payableTotal: pricingQuote.payableTotal,
  paymentInitiated: true, 
  txnid: initData.txnid 
});

      const EasebuzzCheckout = await loadEasebuzzCheckout();
      const easebuzzCheckout = new EasebuzzCheckout(initData.key, initData.env || "test");

      easebuzzCheckout.initiatePayment({
        access_key: initData.access_key,
        onResponse: async (response) => {
          if (response?.status === "success") {
            try {
              const txnid = response.txnid || initData.txnid;
              await confirmBooking(txnid, response.easepayid, booking.blockId);
              setConfirmedTxn(txnid);
              setShowSuccess(true);
            } catch {
              setPayError("Payment succeeded, but ticket confirmation failed. Please contact support with your transaction ID.");
            } finally {
              setPaying(false);
            }
            return;
          }

          setPayError(`Payment ${response?.status || "failed"}. Please try again.`);
          setPaying(false);
        },
        theme: "#0ea5a0",
      });

    } catch (err) {
      console.error("Payment error:", err);
      setPayError(err?.message ?? "Payment failed. Please try again.");
      setPaying(false);
    }
  };

  // ── Empty state ────────────────────────────────────────────────────────
  if (!booking) {
    return (
      <main className="bc-page bc-empty">
        <AlertCircle size={48} color="#e05252" />
        <h2>No booking found</h2>
        <p>Please select a bus and complete traveller details first.</p>
        <button onClick={() => navigate("/buses")}>Search Buses</button>
      </main>
    );
  }

  const { bus, route, seats = [], price = {}, travellers = [], contact = {} } = booking;
  const numPassengers = booking.passengerCount ?? seats.length ?? 0;
  const grandTotal    = Number(price.grandTotal ?? 0);
  // Final customer payable — server-priced quote; payment stays blocked until
  // it loads so an estimate is never presented as the confirmed price.
  const finalPayable  = pricingQuote ? Number(pricingQuote.payableTotal) : null;
  const extraLines    = (pricingQuote?.breakdown || []).filter((l) => Number(l.amount) > 0);
  const seatLabel =
  seats
    .map((s) => `${s.code}(${s.seatType || s.label || bus?.seatType || "General"})`)
    .join(", ");
  const timerPct      = (countdown.remaining / (10 * 60)) * 100;

  return (
    <>
      {/* ── Success Popup ── */}
      {showSuccess && (
        <SuccessPopup
          booking={booking}
          txnid={confirmedTxn}
          onClose={() => { setShowSuccess(false); navigate("/profile?tab=bus"); }}
        />
      )}

      <main className="bc-page">

        {/* ── Top Bar ── */}
        <header className="bc-topbar">
          <button
            className="bc-back-btn"
            onClick={() => navigate("/busbooking", { state: { booking } })}
            disabled={paying}
          >
            <ArrowLeft size={16} /> Back
          </button>

          <nav className="bc-steps" aria-label="Booking steps">
            <span className="bc-step bc-step--done">
              <span className="bc-step-num">✓</span> Travellers
            </span>
            <span className="bc-step-arrow">›</span>
            <span className="bc-step bc-step--active">
              <span className="bc-step-num">2</span> Review &amp; Pay
            </span>
          </nav>

          <div className={`bc-timer ${countdown.expired ? "bc-timer--expired" : countdown.remaining < 120 ? "bc-timer--warn" : ""}`}>
            <Clock size={14} />
            {countdown.expired ? "Session expired" : `${countdown.display} left`}
            {!countdown.expired && (
              <span className="bc-timer-bar">
                <span className="bc-timer-fill" style={{ width: `${timerPct}%` }} />
              </span>
            )}
          </div>
        </header>

        {/* ── Layout ── */}
        <div className="bc-layout">

          {/* ── LEFT: Cards ── */}
          <div className="bc-main">

            {/* Trip Summary */}
            <section className="bc-card" aria-label="Trip summary">
              <div className="bc-card-head">
                <Bus size={16} /> Trip Summary
              </div>

              <div className="bc-trip-hero">
                <div className="bc-trip-city">
                  <span className="bc-city-name">{splitCity(route.from)}</span>
                  <span className="bc-city-time">{bus.departure}</span>
                </div>

                <div className="bc-trip-middle">
                  <div className="bc-trip-line">
                    <span className="bc-dot bc-dot--from" />
                    <span className="bc-trip-dash" />
                    <Bus size={18} className="bc-bus-ico" />
                    <span className="bc-trip-dash" />
                    <span className="bc-dot bc-dot--to" />
                  </div>
                  <span className="bc-trip-dur">{bus.duration}</span>
                  <span className="bc-trip-date">{formatDate(route.date)}</span>
                </div>

                <div className="bc-trip-city bc-trip-city--right">
                  <span className="bc-city-name">{splitCity(route.to)}</span>
                  <span className="bc-city-time">{bus.arrival}</span>
                </div>
              </div>

              <div className="bc-trip-meta">
                <div className="bc-meta-chip">
                  <Ticket size={13} />
                  <span>Seats: <strong>{seatLabel || "—"}</strong></span>
                </div>
                <div className="bc-meta-chip">
                  <Users size={13} />
                  <span><strong>{numPassengers}</strong> Passenger{numPassengers !== 1 ? "s" : ""}</span>
                </div>
                <div className="bc-meta-chip">
                  <span className="bc-operator-badge">{bus.operator}</span>
                  <span className="bc-bustype">{bus.busType}</span>
                </div>
              </div>

              <div className="bc-boarding-row">
                <div className="bc-boarding-point">
                  <MapPin size={14} className="bc-pin bc-pin--green" />
                  <div>
                    <span className="bc-bp-label">Boarding</span>
                    <strong className="bc-bp-val">{bus.departure} · {extractPoint(route.boarding) || splitCity(route.from)}</strong>
                  </div>
                </div>
                <div className="bc-boarding-divider" />
                <div className="bc-boarding-point">
                  <MapPin size={14} className="bc-pin bc-pin--red" />
                  <div>
                    <span className="bc-bp-label">Dropping</span>
                    <strong className="bc-bp-val">{bus.arrival} · {extractPoint(route.dropping) || splitCity(route.to)}</strong>
                  </div>
                </div>
              </div>
            </section>

            {/* Traveller Details */}
            <section className="bc-card" aria-label="Traveller details">
              <div className="bc-card-head">
                <Users size={16} /> Traveller Details
              </div>

              <div className="bc-travellers">
                {travellers.length > 0 ? travellers.map((t, i) => (
                  <div key={i} className="bc-traveller-row">
                    <div className="bc-trav-avatar">{(t.firstName?.[0] ?? "T").toUpperCase()}</div>
                    <div className="bc-trav-info">
                      <strong>{t.title} {t.firstName} {t.lastName}</strong>
                      <span>Age {t.age} · {["Mrs", "Ms"].includes(t.title) ? "Female" : "Male"}</span>
                    </div>
                    <div className="bc-trav-seat">
                      <Ticket size={12} />
                      {t.seat?.code ?? seats[i]?.code ?? "—"}
                    </div>
                  </div>
                )) : <p className="bc-no-data">No traveller data.</p>}

                <div className="bc-contact-row">
                  <span>✉ {contact.email || "—"}</span>
                  <span>📱 {contact.phone ? `+91 ${contact.phone}` : "—"}</span>
                </div>
              </div>
            </section>

            {/* Payment */}
            <section className="bc-card bc-payment-card" aria-label="Payment">
              <div className="bc-card-head bc-card-head--spaced">
                <span><CreditCard size={16} /> Complete Payment</span>
                <span className={`bc-timer-inline ${countdown.expired ? "bc-timer--expired" : countdown.remaining < 120 ? "bc-timer--warn" : ""}`}>
                  <Clock size={12} /> {countdown.expired ? "Expired" : countdown.display}
                </span>
              </div>

              <div className="bc-pay-body">
                <p className="bc-pay-info">
                  You'll be redirected to <strong>Easebuzz</strong> secure checkout. All major payment methods accepted.
                </p>

                <div className="bc-pay-methods">
                  <div className="bc-pm-pill"><CreditCard size={14} /> Cards</div>
                  <div className="bc-pm-pill"><Smartphone size={14} /> UPI</div>
                  <div className="bc-pm-pill"><Landmark size={14} /> Net Banking</div>
                  <div className="bc-pm-pill"><Wallet size={14} /> Wallets</div>
                </div>

                {payError && (
                  <div className="bc-error-banner" role="alert">
                    <AlertCircle size={15} /> {payError}
                  </div>
                )}

                <button
                  className="bc-pay-btn"
                  disabled={paying || countdown.expired || finalPayable === null}
                  onClick={handlePay}
                >
                  {paying
                    ? <><span className="bc-spinner" />Redirecting to Easebuzz…</>
                    : countdown.expired
                      ? "Session Expired — Go Back"
                      : finalPayable === null
                        ? pricingError
                          ? "Pricing Failed — Go Back"
                          : "Preparing final price…"
                        : `Pay ${money(finalPayable)} securely →`}
                </button>

                <p className="bc-secure-note">
                  <ShieldCheck size={13} /> 256-bit SSL encrypted · PCI-DSS compliant · 100% secure
                </p>
              </div>
            </section>
          </div>

          {/* ── RIGHT: Price Summary ── */}
          <aside className="bc-price-card">
            <div className="bc-card-head">Price Summary</div>

            <div className="bc-price-rows">
  <div className="bc-price-row">
    <span>Supplier Fare</span>
    <strong>{money(pricingQuote?.supplier?.fareTotal ?? grandTotal)}</strong>
  </div>

  {(pricingQuote?.breakdown || [])
    .filter((line) => Number(line.amount) > 0)
    .map((line) => (
      <div className="bc-price-row" key={line.code}>
        <span>{line.label || line.name || line.code}</span>
        <strong>{money(line.amount)}</strong>
      </div>
    ))}

  <div className="bc-price-row bc-price-row--total">
    <span>Total Payable</span>
    <strong>
      {finalPayable !== null ? money(finalPayable) : "—"}
    </strong>
  </div>
</div>

            {pricingError && (
              <div className="bc-error-banner" role="alert" style={{ marginTop: 10 }}>
                <AlertCircle size={15} /> {pricingError} — please go back and try again.
              </div>
            )}

            <div className="bc-secure-badge">
              <ShieldCheck size={14} /> 100% Secure Payment
            </div>

            <div className="bc-cancellation-note">
              <AlertCircle size={13} />
              <span>Cancellation within 24h of booking available from your profile.</span>
            </div>
          </aside>
        </div>
      </main>


    </>
  );
}
